/**
 * The Reports page's list, as plain data: which reports there are, which
 * labels each carries, and what a view and a search leave on screen.
 *
 * Kept free of React so the rules are tested as rules. Everything here runs in
 * the browser over the ONE page of reports the API returns (up to 200) — the
 * exception to G13 recorded in docs/decisions.md: a label's count has to follow
 * the search, and the reports and labels come from two endpoints, so neither
 * can do it alone. Past 200 the page says so, and search moves to the server.
 */
import type { SavedReport } from '@clockwise/clients/report-definitions';
import type { ReportLabel, ReportLabelItem } from '@clockwise/clients/report-labels';
import type { BuiltInReportKey } from '@clockwise/shared';
import { format } from 'date-fns';

/** Reports that ship with the product. Keyed as the API keys them. */
export const BUILT_IN_REPORTS: readonly {
  key: BuiltInReportKey;
  name: string;
  description: string;
  to: '/reports/timesheets';
}[] = [
  {
    key: 'timesheets',
    name: 'Timesheet report',
    description:
      'Timesheet entries across employees and customers for a date range and status, exportable to Excel.',
    to: '/reports/timesheets',
  },
];

/** One row of the list — a saved report or a built-in, told apart by `saved`. */
export interface LibraryEntry {
  /** Stable across both kinds: a report id, or `builtin:<key>`. */
  key: string;
  name: string;
  description: string | null;
  /** The saved report, or null for a built-in. */
  saved: SavedReport | null;
  builtinKey: BuiltInReportKey | null;
  /** Ids of every label on this entry the caller can see, Starred included. */
  labelIds: ReadonlySet<string>;
}

/**
 * Where the list is looking. The five fixed views, or one label by id.
 * Serialised into the URL as-is (`?view=label:<id>`), so a view is a link.
 *
 * `shared` is what OTHERS shared with the caller; `shared-by-you` is the other
 * direction — the caller's own reports that somebody else can run.
 */
export type LibraryView =
  | 'all'
  | 'starred'
  | 'mine'
  | 'shared-by-you'
  | 'shared'
  | `label:${string}`;

export const FIXED_VIEWS = ['all', 'starred', 'mine', 'shared-by-you', 'shared'] as const;

/** The longest search kept — the box stops there and the URL is trimmed to it. */
export const MAX_SEARCH = 200;

export function isLibraryView(v: string): v is LibraryView {
  return (FIXED_VIEWS as readonly string[]).includes(v) || /^label:[0-9a-f-]{36}$/i.test(v);
}

/** Join the two API answers into rows: every built-in, then every saved report. */
export function buildEntries(
  reports: readonly SavedReport[],
  items: readonly ReportLabelItem[],
): LibraryEntry[] {
  const byTarget = new Map<string, Set<string>>();
  for (const item of items) {
    const key = item.reportId ?? `builtin:${item.builtinKey}`;
    const set = byTarget.get(key) ?? new Set<string>();
    set.add(item.labelId);
    byTarget.set(key, set);
  }
  const entries: LibraryEntry[] = [
    ...BUILT_IN_REPORTS.map((b) => ({
      key: `builtin:${b.key}`,
      name: b.name,
      description: b.description,
      saved: null,
      builtinKey: b.key,
      labelIds: byTarget.get(`builtin:${b.key}`) ?? new Set<string>(),
    })),
    ...reports.map((r) => ({
      key: r.id,
      name: r.name,
      description: r.description,
      saved: r,
      builtinKey: null,
      labelIds: byTarget.get(r.id) ?? new Set<string>(),
    })),
  ];
  // By name, the way people scan for a title they half remember — the same
  // order the API lists saved reports in, extended over the built-ins.
  return entries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

/** The caller's Starred label, if they have starred anything yet. */
export function starredLabelOf(labels: readonly ReportLabel[]): ReportLabel | undefined {
  return labels.find((l) => l.kind === 'starred');
}

/** Is this entry in this view? Search is applied separately, by {@link matches}. */
export function inView(
  entry: LibraryEntry,
  view: LibraryView,
  starredId: string | undefined,
): boolean {
  switch (view) {
    case 'all':
      return true;
    case 'starred':
      return starredId !== undefined && entry.labelIds.has(starredId);
    case 'mine':
      return entry.saved?.owned === true;
    case 'shared-by-you':
      // `owned` as well as a share list: only an owner is ever sent one, and
      // the check is here so that stays true whatever a response carries.
      return entry.saved?.owned === true && entry.saved.shares.length > 0;
    case 'shared':
      return entry.saved !== null && !entry.saved.owned;
    default:
      return entry.labelIds.has(view.slice('label:'.length));
  }
}

/**
 * Does the search find this entry? Case-insensitive, across the name, the
 * description and the names of its labels — so typing "utilization" finds the
 * reports filed under Utilization even when the word is in none of their
 * titles. Starred is not a word anybody searches for, so it is left out.
 */
export function matches(
  entry: LibraryEntry,
  q: string,
  labelNames: ReadonlyMap<string, string>,
): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const hay = [entry.name, entry.description ?? ''];
  for (const id of entry.labelIds) {
    const name = labelNames.get(id);
    if (name) hay.push(name);
  }
  return hay.some((h) => h.toLowerCase().includes(needle));
}

/** What the list shows: the view, then the search. */
export function visibleEntries(
  entries: readonly LibraryEntry[],
  view: LibraryView,
  q: string,
  labels: readonly ReportLabel[],
): LibraryEntry[] {
  const starredId = starredLabelOf(labels)?.id;
  const names = labelNameMap(labels);
  return entries.filter((e) => inView(e, view, starredId) && matches(e, q, names));
}

/**
 * How many entries each view would show for the CURRENT search — the numbers
 * beside the rail's items, so the rail says where the matches are.
 */
export function viewCounts(
  entries: readonly LibraryEntry[],
  q: string,
  labels: readonly ReportLabel[],
): Map<LibraryView, number> {
  const starredId = starredLabelOf(labels)?.id;
  const names = labelNameMap(labels);
  const found = entries.filter((e) => matches(e, q, names));
  const counts = new Map<LibraryView, number>();
  const views: LibraryView[] = [...FIXED_VIEWS, ...labels.map((l) => `label:${l.id}` as const)];
  for (const v of views) {
    counts.set(v, found.filter((e) => inView(e, v, starredId)).length);
  }
  return counts;
}

/**
 * The labels the rail lists, in the order it lists them: the caller's own
 * first, then the standard ones. Starred is not here — it is a fixed view.
 *
 * A standard label nobody has filed anything under FOR THIS CALLER is left
 * out, unless they manage standard labels: to a reader it is a heading over
 * nothing, and to a manager it is something they are about to fill.
 */
export function railLabels(
  labels: readonly ReportLabel[],
  entries: readonly LibraryEntry[],
  canManageStandard: boolean,
): { personal: ReportLabel[]; standard: ReportLabel[] } {
  const byName = (a: ReportLabel, b: ReportLabel) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  const used = new Set(entries.flatMap((e) => [...e.labelIds]));
  return {
    personal: labels.filter((l) => l.kind === 'personal').sort(byName),
    standard: labels
      .filter((l) => l.kind === 'standard' && (canManageStandard || used.has(l.id)))
      .sort(byName),
  };
}

function labelNameMap(labels: readonly ReportLabel[]): Map<string, string> {
  return new Map(labels.filter((l) => l.kind !== 'starred').map((l) => [l.id, l.name]));
}

/**
 * Does the rail offer "Shared by you" to this caller?
 *
 * To someone who MAY share (`report.share`), and to someone who HAS — a grant
 * taken away later does not un-share what was already shared, and its owner
 * still needs to find it. To anyone else it is a heading over a list that can
 * never fill, with nothing they could do about it; the same reasoning that
 * hides an empty standard label from a reader in {@link railLabels}.
 *
 * Decided over EVERY entry, not the search's matches, so the item does not
 * come and go while the caller types.
 */
export function offersSharedByYou(entries: readonly LibraryEntry[], canShare: boolean): boolean {
  return canShare || entries.some((e) => inView(e, 'shared-by-you', undefined));
}

/**
 * Which Reports page this caller gets — the labelled library, or the original
 * cards — while the new one is rolled out to a few people first.
 *
 * The library goes to anyone who may BUILD reports (`report.edit`) and to
 * anyone who has had a report shared with them: the two groups for whom a list
 * of saved reports is the point of the page. Everyone else keeps the original
 * page, which for them is the Timesheet report and little else. Widening the
 * rollout is a grant of `report.edit` or a share, not a deploy.
 *
 * Decided from the same list request both pages make, so the choice costs no
 * extra round trip. While that is loading the original page's skeleton shows
 * for someone without `report.edit`; a caller holding it is known up front.
 */
export function usesReportsLibrary(
  permissions: ReadonlySet<string>,
  saved: readonly SavedReport[] | undefined,
): boolean {
  return permissions.has('report.edit') || (saved?.some((r) => !r.owned) ?? false);
}

/**
 * When a report was last changed, compactly — "now", "5m ago", "3h ago",
 * "2d ago", "3w ago", then "Mar 4". Compact on purpose: it sits in a narrow
 * column on every row, and date-fns' distance helpers spell the unit out
 * ("2 days ago").
 */
export function updatedAgo(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const minutes = Math.max(0, Math.round((now.getTime() - then.getTime()) / 60_000));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 35) return `${Math.round(days / 7)}w ago`;
  return format(then, 'MMM d');
}
